import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SendHorizonal,
  ArrowRightLeft,
  GitCommitVertical,
  UserCheck,
  ClipboardList
} from 'lucide-react';
import {
  Badge,
  Button,
  Switch,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserCard,
  SystemCard,
  OrgFunctionCard,
  ExternalContactCard,
  getUserFullName
} from '@bliks-io/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { useStandardRecordingTimeMs } from '@/hooks/useStandardRecordingTime';
import {
  type AssigneeRef,
  type PopulatedSystem,
  type PopulatedOrgFunction,
  type PopulatedExternalContact,
  type RecordingDashboardFlatStep,
  type RecordingDashboardSurvey
} from '@bliks-io/contracts';
import { DueDateCell } from '@/lib/due-date-utils';
import { toUserCardProps, type NudgeTarget } from './types';

// ── Sub-row union type ──────────────────────────────────────────────

type SubRow =
  | { type: 'step'; step: RecordingDashboardFlatStep }
  | { type: 'survey'; survey: RecordingDashboardSurvey };

// ── Sort by due date ────────────────────────────────────────────────

function buildSortedSubRows(
  steps: RecordingDashboardFlatStep[],
  surveys: RecordingDashboardSurvey[]
): SubRow[] {
  const allRows: SubRow[] = [
    ...steps.map((step) => ({ type: 'step' as const, step })),
    ...surveys.map((survey) => ({ type: 'survey' as const, survey }))
  ];

  return allRows.sort((a, b) => {
    const dueDateA = a.type === 'step' ? a.step.dueDate : a.survey.dueDate;
    const dueDateB = b.type === 'step' ? b.step.dueDate : b.survey.dueDate;
    if (dueDateA == null && dueDateB == null) return 0;
    if (dueDateA == null) return 1;
    if (dueDateB == null) return -1;
    return dueDateA - dueDateB;
  });
}

// ── Survey status helper ────────────────────────────────────────────

/**
 * The dismissed state is shown on the row of the performer who declined (ADR
 * 0016 decision 3, amended): the process manager has to correct the performer
 * set, and a bare count would not say whom to remove. The variant free texts
 * never reach this table.
 */
const SURVEY_STATUS_BADGE: Record<
  RecordingDashboardSurvey['status'],
  { colorScheme: 'success' | 'warning' | 'neutral'; labelKey: string }
> = {
  completed: { colorScheme: 'success', labelKey: 'recordingDashboard.surveys.completed' },
  pending: { colorScheme: 'warning', labelKey: 'recordingDashboard.surveys.pending' },
  dismissed: { colorScheme: 'neutral', labelKey: 'recordingDashboard.surveys.dismissed' }
};

// ── Step status helper ──────────────────────────────────────────────

function getStepStatus(step: RecordingDashboardFlatStep) {
  if (step.isCompleted)
    return { colorScheme: 'success' as const, statusKey: 'recordingDashboard.progress.completed' };
  if (step.isOverdue)
    return { colorScheme: 'error' as const, statusKey: 'recordingDashboard.notStarted.overdue' };
  if (step.assignmentId && !step.recordingId)
    return {
      colorScheme: 'warning' as const,
      statusKey: 'recordingDashboard.progress.pendingAssignment'
    };
  return { colorScheme: 'neutral' as const, statusKey: 'recordingDashboard.progress.inRecording' };
}

// ── Assignee cell ───────────────────────────────────────────────────

function AssigneeCell({
  assignee,
  unassignedLabel
}: {
  assignee: AssigneeRef | null;
  unassignedLabel: string;
}) {
  if (!assignee) {
    return <span className="text-xs text-muted-foreground">{unassignedLabel}</span>;
  }

  switch (assignee.model) {
    case 'User':
      return <UserCard user={toUserCardProps(assignee.user)} variant="with-role" size="sm" />;
    case 'System': {
      const system = {
        _id: assignee.system._id,
        name: assignee.system.name,
        category: assignee.system.category,
        logo: assignee.system.logo,
        systemOwner: ''
      } as unknown as PopulatedSystem;
      return <SystemCard system={system} variant="with-category" size="sm" />;
    }
    case 'OrgFunction': {
      const orgFunction = {
        _id: assignee.orgFunction._id,
        name: assignee.orgFunction.name,
        kind: assignee.orgFunction.kind,
        email: assignee.orgFunction.email ?? '',
        department: assignee.orgFunction.departmentAbbreviation
          ? { abbreviation: assignee.orgFunction.departmentAbbreviation }
          : undefined
      } as unknown as PopulatedOrgFunction;
      return <OrgFunctionCard orgFunction={orgFunction} variant="with-email" size="sm" />;
    }
    case 'ExternalContact': {
      const contact = {
        _id: assignee.contact._id,
        name: assignee.contact.name,
        type: assignee.contact.type ?? ''
      } as unknown as PopulatedExternalContact;
      return <ExternalContactCard contact={contact} variant="with-type" size="sm" />;
    }
  }
}

// ── Component ───────────────────────────────────────────────────────

interface ProcessSubTableProps {
  steps: RecordingDashboardFlatStep[];
  surveys: RecordingDashboardSurvey[];
  onNudge: (target: NudgeTarget) => void;
  onReassign: (step: RecordingDashboardFlatStep) => void;
}

export function ProcessSubTable({ steps, surveys, onNudge, onReassign }: ProcessSubTableProps) {
  const { t } = useTranslation();
  const { authContext } = usePermissions();
  const standardRecordingTimeMs = useStandardRecordingTimeMs();
  const [hideCompleted, setHideCompleted] = useState(false);

  const subRows = useMemo(() => {
    const filteredSteps = hideCompleted ? steps.filter((s) => !s.isCompleted) : steps;
    // Dismissed rows survive this filter although they are terminal: hiding
    // "completed" hides what is finished, and a decline is the opposite — it is
    // the row the manager still has to act on (correct the performer set).
    const filteredSurveys = hideCompleted
      ? surveys.filter((s) => s.status !== 'completed')
      : surveys;
    return buildSortedSubRows(filteredSteps, filteredSurveys);
  }, [steps, surveys, hideCompleted]);

  return (
    <TooltipProvider>
      <div className="bg-muted/30 [&_td]:py-2 [&_th]:py-2">
        <div className="flex items-center justify-end gap-2 px-4 py-2">
          <Label htmlFor="hide-completed" className="text-xs text-muted-foreground">
            {t('recordingDashboard.subTable.hideCompleted')}
          </Label>
          <Switch id="hide-completed" checked={hideCompleted} onCheckedChange={setHideCompleted} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('recordingDashboard.columns.type')}</TableHead>
              <TableHead>{t('recordingDashboard.columns.step')}</TableHead>
              <TableHead>{t('recordingDashboard.columns.assignee')}</TableHead>
              <TableHead>{t('recordingDashboard.columns.status')}</TableHead>
              <TableHead>{t('recordingDashboard.columns.dueDate')}</TableHead>
              <TableHead className="w-0">
                <span className="sr-only">{t('recordingDashboard.columns.actions')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subRows.map((subRow) =>
              subRow.type === 'step' ? (
                <StepRow
                  key={`step-${subRow.step._id}`}
                  step={subRow.step}
                  onNudge={onNudge}
                  onReassign={onReassign}
                  currentUserId={authContext?.userId}
                  standardRecordingTimeMs={standardRecordingTimeMs}
                  t={t}
                />
              ) : (
                <SurveyRow
                  key={`survey-${subRow.survey._id}`}
                  survey={subRow.survey}
                  onNudge={onNudge}
                  standardRecordingTimeMs={standardRecordingTimeMs}
                  t={t}
                />
              )
            )}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

// ── Step row ────────────────────────────────────────────────────────

function StepRow({
  step,
  onNudge,
  onReassign,
  currentUserId,
  standardRecordingTimeMs,
  t
}: {
  step: RecordingDashboardFlatStep;
  onNudge: ProcessSubTableProps['onNudge'];
  onReassign: ProcessSubTableProps['onReassign'];
  currentUserId?: string;
  standardRecordingTimeMs: number;
  t: (key: string) => string;
}) {
  const { colorScheme, statusKey } = getStepStatus(step);
  const isOwner = currentUserId === step.owner._id;
  const isAssignment = !!(step.assignmentId && !step.recordingId);
  const userAssignee = step.assignee?.model === 'User' ? step.assignee.user : null;

  const nudgeButton = step.canNudge && !step.isCompleted && step.assignee && (
    <Button
      variant="ghost"
      size="icon"
      className="cursor-pointer"
      disabled={!userAssignee}
      onClick={() => {
        if (!userAssignee) return;
        onNudge({
          userId: userAssignee._id,
          name: getUserFullName(userAssignee.firstName, userAssignee.lastName),
          processId: step.processId,
          processName: step.processName,
          stepId: step._id
        });
      }}
    >
      <SendHorizonal className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <TableRow>
      <TableCell>
        <Badge colorScheme={isAssignment ? 'warning' : 'neutral'} size="sm">
          <span className="flex items-center gap-1">
            {isAssignment ? (
              <UserCheck className="h-3 w-3" />
            ) : (
              <GitCommitVertical className="h-3 w-3" />
            )}
            {isAssignment
              ? t('recordingDashboard.subTable.assignment')
              : t('recordingDashboard.subTable.step')}
          </span>
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        {step.stepName ||
          t(
            isAssignment
              ? 'recordingDashboard.inRecording.pendingStep'
              : 'recordingDashboard.inRecording.openStep'
          )}
      </TableCell>
      <TableCell>
        <AssigneeCell
          assignee={step.assignee}
          unassignedLabel={t('recordingDashboard.columns.unassigned')}
        />
      </TableCell>
      <TableCell>
        <Badge colorScheme={colorScheme} size="sm">
          {t(statusKey)}
        </Badge>
      </TableCell>
      <TableCell>
        <DueDateCell dueDate={step.dueDate} standardRecordingTimeMs={standardRecordingTimeMs} />
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {nudgeButton &&
            (userAssignee ? (
              nudgeButton
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>{nudgeButton}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {t('recordingDashboard.subTable.cannotNudgeNonPerson')}
                </TooltipContent>
              </Tooltip>
            ))}
          {isOwner && !step.isCompleted && (
            <Button
              variant="ghost"
              size="icon"
              className="cursor-pointer"
              onClick={() => onReassign(step)}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Survey row ──────────────────────────────────────────────────────

function SurveyRow({
  survey,
  onNudge,
  standardRecordingTimeMs,
  t
}: {
  survey: RecordingDashboardSurvey;
  onNudge: ProcessSubTableProps['onNudge'];
  standardRecordingTimeMs: number;
  t: (key: string) => string;
}) {
  return (
    <TableRow>
      <TableCell>
        <Badge colorScheme="neutral" size="sm" variant="outline">
          <span className="flex items-center gap-1">
            <ClipboardList className="h-3 w-3" />
            {t('recordingDashboard.subTable.survey')}
          </span>
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{survey.stepName}</TableCell>
      <TableCell>
        <UserCard user={toUserCardProps(survey.user)} variant="with-role" size="sm" />
      </TableCell>
      <TableCell>
        <Badge colorScheme={SURVEY_STATUS_BADGE[survey.status].colorScheme} size="sm">
          {t(SURVEY_STATUS_BADGE[survey.status].labelKey)}
        </Badge>
      </TableCell>
      <TableCell>
        <DueDateCell dueDate={survey.dueDate} standardRecordingTimeMs={standardRecordingTimeMs} />
      </TableCell>
      <TableCell>
        {survey.canNudge && survey.status === 'pending' && (
          <Button
            variant="ghost"
            size="icon"
            className="cursor-pointer"
            onClick={() =>
              onNudge({
                userId: survey.user._id,
                name: getUserFullName(survey.user.firstName, survey.user.lastName),
                processId: survey.processId,
                processName: survey.processName
              })
            }
          >
            <SendHorizonal className="h-3.5 w-3.5" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
